import type { Basket, BasketItem } from "../basket/store";
import type { ModelCatalog } from "../catalog/schema";
import { t } from "../i18n/t";

const SVG_X = `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`;
const SVG_INFO = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>`;

const STORAGE_KEY_KUNDE = "kb-spf:kunde";
const MAILTO_BODY_LIMIT = 1800; // bytes-ish; mailto bodies > 2 kB are unreliable

type CustomerFields = {
  name: string;
  kundennr: string;
  tel: string;
  email: string;
  message: string;
};

export class AnfrageModal {
  private backdrop: HTMLElement;
  private modal: HTMLElement;
  private fields!: Record<keyof CustomerFields, HTMLInputElement | HTMLTextAreaElement>;

  constructor(
    parent: HTMLElement,
    private catalog: ModelCatalog,
    private basket: Basket,
    private toast: (msg: string) => void,
    private onSent: () => void,
  ) {
    this.backdrop = document.createElement("div");
    this.backdrop.className = "kb-modal-backdrop";
    this.modal = document.createElement("div");
    this.modal.className = "kb-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.backdrop.appendChild(this.modal);
    parent.appendChild(this.backdrop);

    this.backdrop.addEventListener("click", (e) => {
      if (e.target === this.backdrop) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.backdrop.classList.contains("visible")) this.close();
    });

    this.render();
  }

  open() {
    this.refreshParts();
    this.backdrop.classList.add("visible");
    setTimeout(() => {
      (this.modal.querySelector('input[name="name"]') as HTMLInputElement | null)?.focus();
    }, 80);
  }

  close() {
    this.backdrop.classList.remove("visible");
  }

  private render() {
    this.modal.innerHTML = `
      <div class="kb-modal-header">
        <div class="kb-modal-eyebrow">${t("anfrage.eyebrow")}</div>
        <h3 class="kb-modal-title">${t("anfrage.title")}</h3>
        <p class="kb-modal-sub">${t("anfrage.sub")}</p>
        <button class="kb-modal-close" aria-label="schließen">${SVG_X}</button>
      </div>
      <div class="kb-modal-body">
        <div class="kb-modal-section">
          <div class="kb-modal-section-label">${t("anfrage.parts")}</div>
          <div class="kb-modal-parts" data-role="parts"></div>
        </div>
        <div class="kb-modal-section">
          <div class="kb-modal-section-label">${t("anfrage.contact")}</div>
          <div class="kb-modal-form">
            <div class="kb-field">
              <label>${t("anfrage.field.name")}</label>
              <input type="text" name="name" autocomplete="name" />
            </div>
            <div class="kb-field">
              <label>${t("anfrage.field.kundennr")}</label>
              <input type="text" name="kundennr" autocomplete="off" />
            </div>
            <div class="kb-field">
              <label>${t("anfrage.field.tel")}</label>
              <input type="tel" name="tel" autocomplete="tel" />
            </div>
            <div class="kb-field">
              <label>${t("anfrage.field.email")}</label>
              <input type="email" name="email" autocomplete="email" />
            </div>
            <div class="kb-field span-2">
              <label>${t("anfrage.message.label")}</label>
              <textarea name="message" rows="3" placeholder="${t(
                "anfrage.message.placeholder",
              )}"></textarea>
            </div>
          </div>
          <div class="kb-modal-hint">${SVG_INFO}<span>${t("anfrage.sub")}</span></div>
        </div>
      </div>
      <div class="kb-modal-footer">
        <button class="kb-modal-cancel">${t("anfrage.cancel")}</button>
        <button class="kb-modal-send">${t("anfrage.send")}</button>
      </div>
    `;

    this.fields = {
      name:    this.modal.querySelector('input[name="name"]')    as HTMLInputElement,
      kundennr:this.modal.querySelector('input[name="kundennr"]')as HTMLInputElement,
      tel:     this.modal.querySelector('input[name="tel"]')     as HTMLInputElement,
      email:   this.modal.querySelector('input[name="email"]')   as HTMLInputElement,
      message: this.modal.querySelector('textarea[name="message"]') as HTMLTextAreaElement,
    };

    this.loadKunde();

    (this.modal.querySelector(".kb-modal-close") as HTMLButtonElement).addEventListener(
      "click",
      () => this.close(),
    );
    (this.modal.querySelector(".kb-modal-cancel") as HTMLButtonElement).addEventListener(
      "click",
      () => this.close(),
    );
    (this.modal.querySelector(".kb-modal-send") as HTMLButtonElement).addEventListener(
      "click",
      () => this.send(),
    );
  }

  private refreshParts() {
    const partsEl = this.modal.querySelector('[data-role="parts"]') as HTMLElement;
    partsEl.innerHTML = "";
    const items = this.basket.list();
    for (const item of items) {
      const row = document.createElement("div");
      row.className = "kb-modal-parts-row";
      row.innerHTML = `
        <div class="kb-modal-parts-info">
          <div class="kb-modal-parts-name">${escapeHtml(item.name)}</div>
          <div class="kb-modal-parts-art">${item.partId}</div>
        </div>
        <div class="kb-qty">
          <button data-act="dec">−</button>
          <input type="number" min="1" max="99" value="${item.qty}" />
          <button data-act="inc">＋</button>
        </div>
      `;
      const qty = row.querySelector('input[type="number"]') as HTMLInputElement;
      const inc = row.querySelector('[data-act="inc"]') as HTMLButtonElement;
      const dec = row.querySelector('[data-act="dec"]') as HTMLButtonElement;
      const update = (n: number) => {
        const v = Math.max(1, Math.min(99, n));
        qty.value = String(v);
        this.basket.setQty(item.partId, v);
      };
      inc.addEventListener("click", () => update(Number(qty.value) + 1));
      dec.addEventListener("click", () => update(Number(qty.value) - 1));
      qty.addEventListener("change", () => update(Number(qty.value) || 1));
      partsEl.appendChild(row);
    }
  }

  private send() {
    const items = this.basket.list();
    if (items.length === 0) return;

    const kunde = this.collectKunde();
    this.saveKunde(kunde);

    const { mailto, fullBody, truncated } = this.buildMailto(items, kunde);

    if (truncated) {
      // Copy the full structured list to clipboard as a safety net.
      try {
        navigator.clipboard?.writeText(fullBody);
      } catch {
        /* clipboard unavailable; mail still works, list is just shorter */
      }
    }

    window.location.href = mailto;
    this.toast(truncated ? t("anfrage.toast.copied") : t("anfrage.toast"));
    this.close();
    this.onSent();
  }

  private collectKunde(): CustomerFields {
    return {
      name: this.fields.name.value.trim(),
      kundennr: this.fields.kundennr.value.trim(),
      tel: this.fields.tel.value.trim(),
      email: this.fields.email.value.trim(),
      message: this.fields.message.value.trim(),
    };
  }

  private loadKunde() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_KUNDE);
      if (!raw) return;
      const k = JSON.parse(raw) as Partial<CustomerFields>;
      if (k.name) this.fields.name.value = k.name;
      if (k.kundennr) this.fields.kundennr.value = k.kundennr;
      if (k.tel) this.fields.tel.value = k.tel;
      if (k.email) this.fields.email.value = k.email;
      // Don't pre-fill message — that's per-request.
    } catch {
      /* ignore */
    }
  }

  private saveKunde(k: CustomerFields) {
    try {
      const stripped = { name: k.name, kundennr: k.kundennr, tel: k.tel, email: k.email };
      localStorage.setItem(STORAGE_KEY_KUNDE, JSON.stringify(stripped));
    } catch {
      /* ignore */
    }
  }

  private buildMailto(items: readonly BasketItem[], k: CustomerFields) {
    const bedName = this.catalog.name.de;

    const partsBlock = items.map((i) => `${i.qty}x ${i.name} (${i.partId})`);
    const customerBlock: string[] = [];
    if (k.name) customerBlock.push(`Name: ${k.name}`);
    if (k.kundennr) customerBlock.push(`Kundennr.: ${k.kundennr}`);
    if (k.tel) customerBlock.push(`Tel.: ${k.tel}`);
    if (k.email) customerBlock.push(`E-Mail: ${k.email}`);

    const buildBody = (parts: string[]) => {
      const lines: string[] = [];
      lines.push(t("anfrage.body.greeting"));
      lines.push("");
      lines.push(t("anfrage.body.lead"));
      lines.push("");
      lines.push(`${t("anfrage.body.bed")}: ${bedName}`);
      lines.push("");
      lines.push(`-- ${t("anfrage.body.parts")} --`);
      lines.push(...parts);
      if (customerBlock.length > 0) {
        lines.push("");
        lines.push(`-- ${t("anfrage.body.customer")} --`);
        lines.push(...customerBlock);
      }
      if (k.message) {
        lines.push("");
        lines.push(`-- ${t("anfrage.body.message")} --`);
        lines.push(k.message);
      }
      lines.push("");
      lines.push(t("anfrage.body.signoff"));
      return lines.join("\n");
    };

    const fullBody = buildBody(partsBlock);
    let body = fullBody;
    let truncated = false;

    if (encodeURIComponent(body).length > MAILTO_BODY_LIMIT) {
      // Truncate to the first N parts that fit, append a note.
      let n = partsBlock.length;
      while (n > 0) {
        const trimmed = partsBlock.slice(0, n);
        const remaining = partsBlock.length - n;
        const note =
          remaining > 0 ? [t("anfrage.body.truncated", { n: remaining })] : [];
        const candidate = buildBody([...trimmed, ...note]);
        if (encodeURIComponent(candidate).length <= MAILTO_BODY_LIMIT) {
          body = candidate;
          truncated = remaining > 0;
          break;
        }
        n--;
      }
    }

    const subject = t("anfrage.subject", { bed: bedName });
    const mailto = `mailto:${t("anfrage.recipient")}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;

    return { mailto, fullBody, truncated };
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
